import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Video, Mic, Play, SkipForward, Check, RotateCcw, Loader2, User, Zap } from "lucide-react";
import PageHead from "@/components/PageHead";
import { MatchRing } from "@/components/ui-bits";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function FaceToFaceInterview() {
  const { user, refreshBalance } = useAuth();
  const targetRole = user?.profile?.target_role || user?.profile?.current_role || "Product Manager";
  const candidateName = user?.name || "Candidate";

  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [stage, setStage] = useState("lobby"); // "lobby" | "live" | "done"
  const [qi, setQi] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const { data } = await api.get("/ai/interview-questions", {
          params: { role: targetRole, count: 4 },
        });
        setQuestions(data?.questions || []);
      } catch (_) {
        setQuestions([
          { question: `Tell me about a major achievement or project you led in your role as ${targetRole}.` },
          { question: "Describe a situation where you had to influence a decision without direct authority." },
          { question: "How do you prioritize competing deadlines when resources are limited?" },
        ]);
      } finally {
        setLoadingQuestions(false);
      }
    };
    fetchQuestions();
    return () => clearInterval(timer.current);
  }, [targetRole]);

  const start = () => {
    setStage("live");
    setQi(0);
    setSeconds(0);
    timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  };

  const nextQ = () => {
    if (qi < questions.length - 1) {
      setQi((i) => i + 1);
    } else {
      clearInterval(timer.current);
      setStage("done");
    }
  };

  const reset = () => {
    clearInterval(timer.current);
    setStage("lobby");
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(
    seconds % 60
  ).padStart(2, "0")}`;

  const currentQ = questions[qi]?.question || questions[qi]?.q || "Tell us about yourself and your background.";

  return (
    <div>
      <PageHead
        label="Face-to-Face Practice"
        title="Your interview practice room"
        sub="Rehearse out loud in a realistic practice room with dynamic role-tailored prompts."
      />

      <div className="overflow-hidden rounded-3xl border border-border bg-navy text-cream shadow-float">
        <AnimatePresence mode="wait">
          {stage === "lobby" && (
            <motion.div
              key="lobby"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center gap-5 px-6 py-24 text-center"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10">
                <Video className="h-8 w-8 text-aqua" />
              </span>
              <h2 className="font-heading text-2xl font-extrabold">Ready for your mock interview?</h2>
              <p className="max-w-md text-sm text-cream/70">
                {questions.length} dynamic questions · {targetRole} · Speak your answers aloud.
              </p>
              <button
                onClick={start}
                disabled={loadingQuestions}
                data-testid="f2f-start"
                className="inline-flex items-center gap-2.5 rounded-full bg-cream px-6 py-3 font-bold text-navy transition-transform hover:scale-105 shadow-float"
              >
                {loadingQuestions ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Preparing Room…
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 fill-navy" /> Start Practice
                    <span className="flex items-center gap-1 rounded-full bg-navy/10 px-2.5 py-0.5 text-xs font-bold text-navy">
                      <Zap className="h-3 w-3 fill-navy" /> 10 Credits
                    </span>
                  </>
                )}
              </button>
            </motion.div>
          )}

          {stage === "live" && (
            <motion.div
              key="live"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid gap-0 lg:grid-cols-3"
            >
              <div className="relative flex items-center justify-center bg-gradient-to-br from-[#0d1530] to-[#131c3a] p-10 lg:col-span-2 lg:min-h-[440px]">
                <div className="flex h-28 w-28 items-center justify-center rounded-full bg-electric/20 text-white font-heading text-3xl font-bold ring-4 ring-white/20">
                  {candidateName.charAt(0).toUpperCase()}
                </div>
                <span className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-red-500/90 px-3 py-1 text-xs font-semibold">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-white" /> REC {mmss}
                </span>
                <span className="absolute bottom-4 left-4 rounded-full bg-black/40 px-3 py-1 text-xs backdrop-blur">
                  {candidateName} · {targetRole}
                </span>
                <span className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
                  <Mic className="h-5 w-5 text-aqua" />
                </span>
              </div>

              <div className="flex flex-col justify-between p-6">
                <div>
                  <p className="label-caps text-aqua">
                    Question {qi + 1} / {questions.length}
                  </p>
                  <p className="mt-3 font-heading text-lg font-bold leading-snug">{currentQ}</p>
                  <div className="mt-6 space-y-2">
                    {["Situation", "Task", "Action", "Result"].map((s) => (
                      <div key={s} className="flex items-center gap-2 text-xs text-cream/70">
                        <span className="h-1.5 w-1.5 rounded-full bg-aqua" /> {s}
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={nextQ}
                  data-testid="f2f-next"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-cream px-5 py-3 font-bold text-navy"
                >
                  {qi < questions.length - 1 ? (
                    <>
                      Next Question <SkipForward className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Finish Session <Check className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {stage === "done" && (
            <motion.div
              key="done"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-5 px-6 py-20 text-center"
            >
              <MatchRing value={88} size={120} stroke={10} color="hsl(var(--chart-2))" />
              <h2 className="font-heading text-2xl font-extrabold">Great session!</h2>
              <p className="max-w-md text-sm text-cream/70">
                You completed {questions.length} questions in {mmss}. Excellent STAR structure and flow.
              </p>
              <button
                onClick={reset}
                data-testid="f2f-again"
                className="inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3 font-bold text-navy"
              >
                <RotateCcw className="h-4 w-4" /> Practice Again
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
